import { BayData, DisplayMode } from '@amzn/ihm-floorplan-core';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { DisplayModeOptions } from './displayModeHelper';
import getColorForText from './zoneMapColorHelper';
import ZoneMapLegend from './zoneMapLegend';

/**
 * Returns different displayModes and use fixtureIdDisplayMode as default
 */
function useDisplayModes(
  fixtureIdToZoneDataMap: Record<
    string,
    {
      zoneName: string;
      zoneId: string;
      productTypes: string;
      isAgile: boolean;
      zoneNameKey: string;
    }
  >,
  zoneNameKeyMap: Map<string, string>,
): DisplayModeOptions {
  const fixtureIdDisplayMode: DisplayMode = useMemo(
    () => ({
      id: 'FIXTURE_ID',
      name: 'Fixture Id',
      render: (fixture: BayData) => {
        const fixtureStr = fixture.id ?? '';
        const zoneData = fixtureIdToZoneDataMap[fixture.id ?? ''] || null;
        const zoneName = zoneData?.zoneName ?? '';
        const isAgile = zoneData?.isAgile ?? false;
        return {
          displayText: fixtureStr,
          fillColor: getColorForText(zoneName, isAgile),
        };
      },
    }),
    [fixtureIdToZoneDataMap],
  );

  const zoneMode: DisplayMode = useMemo(
    () => ({
      id: 'ZONE',
      name: 'Zone',
      render: (fixture: BayData) => {
        const zoneData = fixtureIdToZoneDataMap[fixture.id ?? ''] || null;
        const zoneName = zoneData?.zoneName ?? '';
        const isAgile = zoneData?.isAgile ?? false;
        return {
          displayText: zoneName,
          fillColor: getColorForText(zoneName, isAgile),
        };
      },
    }),
    [fixtureIdToZoneDataMap],
  );

  const isAgileFixtureMode: DisplayMode = useMemo(
    () => ({
      id: 'IS_AGILE_FIXTURE',
      name: 'Is Agile Fixture',
      render: (fixture: BayData) => {
        const zoneData = fixtureIdToZoneDataMap[fixture.id ?? ''] || null;
        const zoneName = zoneData?.zoneName ?? '';
        const isAgile = zoneData?.isAgile ?? false;
        const isAgileStr = isAgile ? 'Yes' : 'No';
        return {
          displayText: isAgileStr,
          fillColor: getColorForText(zoneName, isAgile),
        };
      },
    }),
    [fixtureIdToZoneDataMap],
  );

  const zoneNameKeyMode: DisplayMode = useMemo(
    () => ({
      id: 'ZONE_KEY',
      name: 'Zone Key',
      render: (fixture: BayData) => {
        const zoneData = fixtureIdToZoneDataMap[fixture.id ?? ''] || null;
        const zoneName = zoneData?.zoneName ?? '';
        const isAgile = zoneData?.isAgile ?? false;
        const zoneNameKey = zoneData?.zoneNameKey ?? '';
        return {
          displayText: zoneNameKey,
          fillColor: getColorForText(zoneName, isAgile),
        };
      },
      legend: ZoneMapLegend({ zoneNameKeyMapping: zoneNameKeyMap }),
    }),
    [fixtureIdToZoneDataMap, zoneNameKeyMap],
  );

  const optionsList = sortBy([fixtureIdDisplayMode, zoneMode, isAgileFixtureMode, zoneNameKeyMode], ['name']);

  const defaultOption = zoneNameKeyMode;

  return {
    optionsList,
    defaultOption,
  };
}

export default useDisplayModes;
